import React, { useState } from "react";
import { Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickProductCardViewSettingsModal from "@/features/pathpick/components/product/PathPickProductCardViewSettingsModal";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import { PathPickProductCardViewSettings } from "@/features/pathpick/types";

interface Props {
  // 비교 표시를 끄면 비교 기준·마진 정렬·매칭 탭이 화면에서 사라진다.
  // 쿼리스트링에 걸려 있던 그 값들은 화면별 필터 훅이 정리해야 해서 호출부에 위임한다.
  onComparisonHidden?: () => void;
  // 저장 대신 이 값을 보여주고 이 핸들러로 돌려준다 (시작 가이드 전용).
  // 미지정이면 사용자 설정(localStorage)을 읽고 쓴다 — 운영 화면 2곳은 넘기지 않는다.
  // 가이드는 튜토리얼이라 거기서 바꾼 값이 실제 설정으로 새어 나가면 안 된다.
  // (`PathPickProductCard` 의 `viewOverride` 와 같은 방식이다. 둘은 항상 같이 와야 해서 한 객체로 묶는다.)
  override?: {
    value: PathPickProductCardViewSettings;
    onApply: (next: PathPickProductCardViewSettings) => void;
  };
  // 모바일 필터 모달처럼 흰 버튼들 사이에 묻히는 자리에서 파란 톤으로 띄우고 폭을 채운다.
  isEmphasized?: boolean;
}

// 상품 찾기와 담은 상품의 "현재 목록 조작 줄"이 공유하는 설정 진입점.
// 모달은 눌렀을 때만 마운트해 열 때마다 저장값으로 드래프트가 초기화되게 한다.
function PathPickProductCardViewSettingsButton({
  onComparisonHidden,
  override,
  isEmphasized = false,
}: Props) {
  const { t } = useI18nHelper();
  const { settings, save } = usePathPickProductCardView();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const value = override?.value ?? settings;

  const handleApply = (next: PathPickProductCardViewSettings) => {
    (override?.onApply ?? save)(next);
    setIsOpen(false);
    if (value.showComparison && !next.showComparison) {
      onComparisonHidden?.();
    }
  };

  return (
    <>
      {/* 한국어 라벨이 길어 medium 기본 패딩(좌우 32px)으로는 옆 정렬 셀렉트에 비해
          과하게 넓어지므로 가로 패딩만 줄인다. 라벨 길이에 맞게 늘어나도록 width 는 두지 않는다. */}
      <AppButton
        variant={"white"}
        onClick={() => setIsOpen(true)}
        // AppButton은 width prop을 스타일보다 뒤에 적용해 chakraProps의 width가 덮인다.
        width={isEmphasized ? "100%" : undefined}
        chakraProps={{
          p: "4px 18px",
          ...(isEmphasized && {
            border: "0.5px solid #1272EF",
            background: "#F0F7FF",
            color: "#1272EF",
            _hover: { background: "#E1EEFE" },
          }),
        }}
      >
        <Text textStyle={"body-sm-bold"}>
          {t("path_pick:card_view_settings.button")}
        </Text>
      </AppButton>
      {isOpen && (
        <PathPickProductCardViewSettingsModal
          isOpen
          onClose={() => setIsOpen(false)}
          value={value}
          onApply={handleApply}
        />
      )}
    </>
  );
}

export default PathPickProductCardViewSettingsButton;
